import React, { memo, useEffect, useRef, useState } from 'react';
import { useFormikContext, useField, ErrorMessage } from 'formik';

import type { CountryData } from 'react-phone-input-2';
import {
  // eslint-disable-next-line import/no-named-default
  default as PhoneInputComponent,
} from 'react-phone-input-2';

import type { SetSharedState, SharedState } from 'src/components/AuthNew/types';
import {
  resetPhoneFormat,
  setPhoneFormat,
  setPhoneForPlaceholder,
} from 'src/components/AuthNew/utils';

import Done from '@lt/components/icons-react/Done';
import { useCountry, useTranslation } from '@lt/localization';
import { useDispatch, useSelector } from 'react-redux';
import { setPhoneMaskAndCountryCode } from 'src/store/client/actions';
import { getIsMobile } from 'src/store/view/selectors';
import { bindTranslatedInputFix } from 'src/utils/translatedInputFix';
import styles from './phoneInput.module.css';
import { localization } from '../helpers/localization';
import { PhoneInputWrapper } from '../styles/styles';
import { validatePhoneNumber } from './phoneValidationUtils';

interface InputProps {
  type: string;
  disabled?: boolean;
}

type Props = {
  setIsContinueBtnDisabled?: (state: boolean) => void;
  setSharedState?: SetSharedState;
  sharedState?: SharedState;
  hint?: string;
  name: string;
  className?: string;
  inputProps?: InputProps;
  renderAppendAddon?: (value: string) => JSX.Element | null;
  customInputStyle?: React.CSSProperties;
};

const ALLOWED_COUNTRIES = [
  'ru', // Россия
  'az', // Азербайджан
  'am', // Армения
  'by', // Беларусь
  'ge', // Грузия
  'eg', // Египет
  'id', // Индонезия
  'kz', // Казахстан
  'kg', // Киргизия
  'ae', // ОАЭ
  'tj', // Таджикистан
  'th', // Таиланд
  'tr', // Турция
  'uz', // Узбекистан
];

export const PhoneInput = memo(
  ({
    setIsContinueBtnDisabled,
    setSharedState,
    sharedState,
    hint,
    name,
    className,
    inputProps,
    renderAppendAddon,
    customInputStyle,
  }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { country } = useCountry();

    const [field] = useField({ name });
    const [formattedPhone, setFormattedPhone] = useState('');

    const { onChange: onChangeFormik, value: formikValue } = field;
    const { setFieldValue, setFieldTouched, touched, errors } =
      useFormikContext();

    const [selectedCountry, setSelectedCountry] = useState(
      sharedState?.country || country || 'ru',
    );
    const [placeholder, setPlaceholder] = useState('');
    const [mask, setMask] = useState('');
    const [focus, setFocus] = useState(false);
    const phoneInputRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    const isMobile = useSelector(getIsMobile);

    /**
     * костыль для нормальной работы position: sticky в некоторых iOS
     * вычисляем ширину враппера и применяем ее к списку со странами ul
     *  */
    useEffect(() => {
      const container = phoneInputRef.current;
      if (!container) return undefined;

      setWidth(container.getBoundingClientRect().width);
      return bindTranslatedInputFix(container);
    }, []);

    useEffect(() => {
      if (field.value) setFormattedPhone(setPhoneFormat(mask, field.value));
    }, [field.value, mask]);

    // задаем начальное состояние страны и маски в сторе
    const handleMount = (_value: string, data: CountryData) => {
      const { countryCode, format } = data;
      const defaultCountry =
        countryCode || sharedState?.country || country || 'ru';
      const defaultMask = format || '+. ... ...-..-.. ';

      dispatch(
        setPhoneMaskAndCountryCode({
          phoneMask: defaultMask,
          countryCode: defaultCountry,
        }),
      );
    };

    const isValidNumber = (
      currentValue: string,
      currentCountryMask: string,
      countryCode: string,
    ): boolean => {
      if (!currentValue || !currentCountryMask) {
        if (setIsContinueBtnDisabled) setIsContinueBtnDisabled(true);
        return false;
      }

      const isValid = validatePhoneNumber(
        currentValue,
        currentCountryMask,
        countryCode,
      );

      if (setIsContinueBtnDisabled) {
        setIsContinueBtnDisabled(!isValid);
      }

      return isValid;
    };

    const onChange = (
      val: string,
      currentCountry: CountryData,
      _,
      formattedValue: string,
    ) => {
      if (setSharedState) {
        if (currentCountry.countryCode !== selectedCountry) {
          const newCountry = currentCountry.countryCode;
          setSelectedCountry(newCountry);
          setSharedState((prev) => ({
            ...prev,
            country: newCountry,
            phone_mask: currentCountry.format,
          }));
          setFieldValue('phoneMask', currentCountry.format);
          setFieldValue('countryCode', newCountry);
        }
        setPlaceholder(
          setPhoneForPlaceholder(
            currentCountry.format,
            currentCountry.dialCode,
          ),
        );
        dispatch(
          setPhoneMaskAndCountryCode({
            phoneMask: currentCountry.format,
            countryCode: currentCountry.countryCode,
          }),
        );
      }
      const phoneFormatted = setPhoneFormat(
        currentCountry.format,
        `+${currentCountry.dialCode}${val}`,
      );
      /**
       * Используется для автозаполнения номера телефона в мобильной версии.
       * Номер телефона заполняется без +7, поэтому добавил такую проверку
       */
      if (
        phoneFormatted !== `+${currentCountry.dialCode}${val}` &&
        Math.abs(resetPhoneFormat(formikValue).length - val.length) > 1
      ) {
        onChangeFormik(phoneFormatted);
        setFieldValue(name, phoneFormatted);
      } else {
        isValidNumber(val, currentCountry.format, currentCountry.countryCode);
        onChangeFormik(formattedValue);
        setFieldValue(name, formattedValue);
      }
    };

    const isWithoutError = (value, currentCountry) => {
      // поскольку эта функция единственная вызывается при первом рендере компонента,
      // устанавливаем в ней маску телефона страны, выбранной по дефолту и плейсхолдер
      if (mask !== currentCountry.format) {
        setMask(currentCountry.format);
      }

      if (!placeholder) {
        setPlaceholder(
          setPhoneForPlaceholder(
            currentCountry.format,
            currentCountry.countryCode,
          ),
        );

        if (setSharedState && !sharedState?.phone_mask) {
          setSharedState((prev) => ({
            ...prev,
            phone_mask: currentCountry.format,
          }));
        }
      }

      if (
        !isValidNumber(
          value,
          currentCountry.format,
          currentCountry.countryCode,
        ) &&
        !focus &&
        touched[name] &&
        value !== ''
      ) {
        // возвращаем true, чтобы не рендерить блок с error-message, встроенный в библиотечный компонент
        // за показ error-message отвечает формик
        return true;
      }
      return true;
    };

    const allowedNumbersCount = () => {
      if (placeholder.replace(/\D/g, '').length > 15)
        return placeholder.replace(/\D/g, '').length;
      return undefined;
    };

    const priority = () => {
      // если номер начинается с 76, 77 или 70, значит он казахский
      // в таком случае выставляем приоритет казахам
      const firstNumbers = formattedPhone.slice(0, 2);
      if (
        firstNumbers === '76' ||
        firstNumbers === '77' ||
        firstNumbers === '70'
      ) {
        if (selectedCountry !== 'kz') setSelectedCountry('kz');
        return { kz: 0, ru: 1 };
      }
      return { kz: 1, ru: 0 };
    };

    const hasError = Boolean(errors[name] && touched[name]);

    const isShowSuccessIcon =
      !focus &&
      touched[name] &&
      formattedPhone &&
      isValidNumber(formattedPhone, mask, selectedCountry);

    const inputClass = isMobile
      ? `${styles.inputClass} notranslate`
      : 'notranslate';

    return (
      <div className={className} ref={phoneInputRef}>
        <div className={styles.inputHeader}>
          {t('checkout.phoneInputForm.header')}
        </div>
        <PhoneInputWrapper $hasError={hasError && !focus} $width={width}>
          <PhoneInputComponent
            specialLabel=""
            enableLongNumbers={allowedNumbersCount()}
            // передаем отформатированный value
            value={formattedPhone}
            disabled={inputProps?.disabled}
            inputProps={{
              name,
              ...inputProps,
              autoComplete: 'tel',
              translate: 'no',
            }}
            enableSearch
            onlyCountries={ALLOWED_COUNTRIES}
            masks={{
              ge: '... .. .. ..', // Кастомная маска для Грузии (9 цифр)
              eg: '.. .... ....', // Кастомная маска для Египта (10 цифр)
              id: '... .... ....', // Кастомная маска для Индонезии (до 11 цифр)
              ae: '.. ... ....', // Кастомная маска для ОАЭ (9 цифр)
              tj: '.. ... .. ..', // Кастомная маска для Таджикистана (9 цифр)
              th: '. .... ....', // Кастомная маска для Таиланда (9 цифр)
            }}
            country={selectedCountry}
            priority={priority()}
            searchPlaceholder={t('checkout.phoneInputForm.searchPlaceholder')}
            searchNotFound={t('checkout.phoneInputForm.searchNotFound')}
            disableSearchIcon
            autocompleteSearch
            localization={localization}
            onChange={onChange}
            placeholder={placeholder}
            onBlur={() => {
              setFocus(false);
              setFieldTouched(name);
            }}
            onFocus={() => {
              setFocus(true);
            }}
            isValid={isWithoutError}
            areaCodes={{ kz: [7, 6, 0] }}
            onMount={handleMount}
            inputClass={inputClass}
            inputStyle={customInputStyle}
          />
          {isShowSuccessIcon && (
            <div className={styles.doneWrapper}>
              <Done className={styles.done} />
            </div>
          )}
        </PhoneInputWrapper>
        {hasError && (
          <div className={styles.inputError}>
            <ErrorMessage name={name} />
            {renderAppendAddon && renderAppendAddon(formattedPhone)}
          </div>
        )}

        {hint && !hasError && <div className={styles.inputHint}>{hint}</div>}
      </div>
    );
  },
);
